import { useCallback, useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { rewardsAPI } from '../../lib/api';
import { t } from '../../lib/i18n';
import { btnDanger, btnGhost, btnPrimary, EmptyState, Field, inputCls, Modal, panelCls, StatusBadge } from '../../components/ui';
import { nyTime } from '../../lib/nyTime';

function ItemModal({ item, onClose, onSaved }) {
  const [form, setForm] = useState(item || {
    title: '', description: '', cost_points: 100, kind: 'manual', credit_amount: 0, active: true,
  });
  const [busy, setBusy] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form, cost_points: Number(form.cost_points), credit_amount: Number(form.credit_amount || 0) };
      if (item) await rewardsAPI.updateItem(item.id, payload);
      else await rewardsAPI.createItem(payload);
      toast.success(t('Saved'));
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Save failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={item ? t('Edit item') : t('New item')} onClose={onClose}>
      <div className="space-y-4">
        <Field label={t('Title')}>
          <input className={inputCls} value={form.title} onChange={(e) => set({ title: e.target.value })} />
        </Field>
        <Field label={t('Description')}>
          <input className={inputCls} value={form.description} onChange={(e) => set({ description: e.target.value })} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('Cost (points)')}>
            <input type="number" min="1" className={inputCls} value={form.cost_points}
              onChange={(e) => set({ cost_points: e.target.value })} />
          </Field>
          <Field label={t('Kind')} hint={t('Credit items self-fulfill; manual items queue for staff')}>
            <select className={inputCls} value={form.kind} onChange={(e) => set({ kind: e.target.value })}>
              <option value="manual">{t('manual fulfillment')}</option>
              <option value="credit">{t('platform credit')}</option>
            </select>
          </Field>
        </div>
        {form.kind === 'credit' && (
          <Field label={t('Credit amount granted')}>
            <input type="number" min="0" step="any" className={inputCls} value={form.credit_amount}
              onChange={(e) => set({ credit_amount: e.target.value })} />
          </Field>
        )}
        <label className="flex items-center gap-2 text-sm text-mms-muted">
          <input type="checkbox" checked={form.active} onChange={(e) => set({ active: e.target.checked })} />
          {t('Active')}
        </label>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button onClick={onClose} className={btnGhost}>{t('Cancel')}</button>
        <button onClick={save} disabled={busy} className={btnPrimary}>{busy ? t('Saving…') : t('Save')}</button>
      </div>
    </Modal>
  );
}

export default function CatalogPage() {
  const [items, setItems] = useState(null);
  const [reds, setReds] = useState(null);
  const [redStatus, setRedStatus] = useState('pending');
  const [editing, setEditing] = useState(undefined);

  const load = useCallback(() => {
    rewardsAPI.adminCatalog().then(({ data }) => setItems(data.items)).catch(() => toast.error(t('Could not load the catalog')));
    rewardsAPI.adminRedemptions({ status: redStatus }).then(({ data }) => setReds(data.items)).catch(() => {});
  }, [redStatus]);
  useEffect(load, [load]);

  const removeItem = async (item) => {
    if (!window.confirm(t('Delete this item?'))) return;
    try {
      await rewardsAPI.deleteItem(item.id);
      toast.success(t('Deleted'));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Delete failed'));
    }
  };

  const resolve = async (r, action) => {
    const note = action === 'cancel' ? (window.prompt(t('Note (optional)')) || '') : '';
    try {
      await rewardsAPI.resolveRedemption(r.id, action, note);
      toast.success(t('Done'));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Action failed'));
    }
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-lg font-semibold">{t('Rewards catalog')}</h1>
        <button onClick={() => setEditing(null)} className={btnPrimary}><Plus size={15} /> {t('New item')}</button>
      </div>

      {items === null ? (
        <p className="text-sm text-mms-muted">{t('Loading…')}</p>
      ) : items.length === 0 ? (
        <EmptyState>{t('No catalog items yet — create the first reward.')}</EmptyState>
      ) : (
        <div className={`${panelCls} overflow-x-auto`}>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-mms-border text-left text-xs text-mms-faint">
                <th className="px-4 py-2.5 font-medium">{t('Title')}</th>
                <th className="px-4 py-2.5 font-medium text-right">{t('Cost (points)')}</th>
                <th className="px-4 py-2.5 font-medium">{t('Kind')}</th>
                <th className="px-4 py-2.5 font-medium">{t('Status')}</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-b border-mms-border/60 last:border-0">
                  <td className="px-4 py-2.5">
                    <div className="font-medium">{it.title}</div>
                    {it.description ? <div className="text-xs text-mms-faint">{it.description}</div> : null}
                  </td>
                  <td className="px-4 py-2.5 text-right mms-num">{it.cost_points}</td>
                  <td className="px-4 py-2.5 text-xs text-mms-muted">
                    {it.kind}{it.kind === 'credit' ? ` (+${it.credit_amount})` : ''}
                  </td>
                  <td className="px-4 py-2.5"><StatusBadge status={it.active ? 'active' : 'inactive'} /></td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    <button onClick={() => setEditing(it)} className={`${btnGhost} mr-2`}>{t('Edit')}</button>
                    <button onClick={() => removeItem(it)} className={btnDanger}>{t('Delete')}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-6 mb-2 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-mms-muted">{t('Redemptions')}</h2>
        <select className={`${inputCls} !w-40`} value={redStatus} onChange={(e) => setRedStatus(e.target.value)}>
          <option value="pending">{t('pending')}</option>
          <option value="fulfilled">{t('fulfilled')}</option>
          <option value="cancelled">{t('cancelled')}</option>
          <option value="">{t('All statuses')}</option>
        </select>
      </div>
      {reds === null ? null : reds.length === 0 ? (
        <EmptyState>{t('No redemptions here.')}</EmptyState>
      ) : (
        <div className={`${panelCls} overflow-x-auto`}>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-mms-border text-left text-xs text-mms-faint">
                <th className="px-4 py-2.5 font-medium">{t('When')}</th>
                <th className="px-4 py-2.5 font-medium">{t('Member')}</th>
                <th className="px-4 py-2.5 font-medium">{t('Item')}</th>
                <th className="px-4 py-2.5 font-medium text-right">{t('Cost (points)')}</th>
                <th className="px-4 py-2.5 font-medium">{t('Status')}</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {reds.map((r) => (
                <tr key={r.id} className="border-b border-mms-border/60 last:border-0">
                  <td className="px-4 py-2.5 whitespace-nowrap text-xs text-mms-muted">
                    {nyTime(r.requested_at)}
                  </td>
                  <td className="px-4 py-2.5">{r.member?.name || '—'}</td>
                  <td className="px-4 py-2.5">{r.item_title}</td>
                  <td className="px-4 py-2.5 text-right mms-num">{r.cost_points}</td>
                  <td className="px-4 py-2.5"><StatusBadge status={r.status} /></td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    {r.status === 'pending' && (
                      <>
                        <button onClick={() => resolve(r, 'fulfill')} className={`${btnGhost} mr-1.5`}>{t('fulfill')}</button>
                        <button onClick={() => resolve(r, 'cancel')} className={btnDanger}>{t('cancel')}</button>
                      </>
                    )}
                    {r.status === 'fulfilled' && (
                      <button onClick={() => resolve(r, 'cancel')} className={btnDanger}>{t('cancel')}</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing !== undefined && (
        <ItemModal item={editing} onClose={() => setEditing(undefined)} onSaved={() => { setEditing(undefined); load(); }} />
      )}
    </div>
  );
}
